import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Skeleton, // Import Skeleton
    useTheme,
    CssBaseline,
    Stack,
    Divider,
    Container,
    Paper
} from '@mui/material';
import {
    AreaChart,
    Area,
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import { useTranslation } from 'react-i18next';

// --- ICONS ---
import ShowChartIcon from '@mui/icons-material/ShowChart';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import HubIcon from '@mui/icons-material/Hub';
import TollIcon from '@mui/icons-material/Toll';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SellIcon from '@mui/icons-material/Sell';
import OdinBalance from '../Components/Analytics/OdinBalance.component';

// --- HELPER FUNCTIONS ---
const formatNumber = (num, locale = 'en-US') => {
    // if (num >= 1e12) return `${ (num / 1e12).toFixed(2) }T`;
    // if (num >= 1e9) return `${ (num / 1e9).toFixed(2) }B`;
    // if (num >= 1e6) return `${ (num / 1e6).toFixed(2) }M`;
    // if (num >= 1e3) return `${ (num / 1e3).toFixed(1) }K`;
    return num.toLocaleString(locale);
};
const formatDate = (dateString, locale = 'en-US') => {
    const date = new Date(dateString);
    return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
};

// --- REUSABLE COMPONENTS ---
export const StatCard = ({ title, value, icon, color }) => (
    <Card sx={{ height: '100%' }}>
        <CardContent>
            <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ backgroundColor: `${ color }20`, color, borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {icon}
                </Box>
                <Box>
                    <Typography variant="body2" color="text.secondary">{title}</Typography>
                    <Typography variant="h6">{value}</Typography>
                </Box>
            </Stack>
        </CardContent>
    </Card>
);

// --- NEW SKELETON COMPONENT ---
export const StatCardSkeleton = () => (
    <Card sx={{ height: '100%' }}>
        <CardContent>
            <Stack direction="row" spacing={2} alignItems="center">
                <Skeleton variant="circular" width={48} height={48} />
                <Box sx={{ flexGrow: 1 }}>
                    <Skeleton variant="text" width="70%" sx={{ mb: 0.5 }} />
                    <Skeleton variant="text" width="40%" />
                </Box>
            </Stack>
        </CardContent>
    </Card>
);


const ChartContainer = ({ title, children }) => (
    <Paper sx={{ p: { xs: 1.5, md: 3 }, height: 380, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>{title}</Typography>
        <Box sx={{ flex: 1 }}>
            {children}
        </Box>
    </Paper>
);

// --- MAIN DASHBOARD COMPONENT ---
function DashboardContent() {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    // Get current locale for number formatting
    const locale = i18n.language === 'zh' ? 'zh-CN' : 'en-US';

    useEffect(() => {
        const processData = async () => {

            try {
                const res = await fetch("https://api.odin.fun/v1/statistics/dashboard");
                const json = await res.json();
                const dates = json.trades_30d.map((t) => t.date).reverse();

                const combined = dates.map((date) => {
                    const t = json.trades_30d.find((x) => x.date === date) || {};
                    const a = json.activities_30d.find((x) => x.date === date) || {};
                    const buys = a.activity_types?.buy?.count || 0;
                    const sells = a.activity_types?.sell?.count || 0;
                    return {
                        date: formatDate(date, locale),
                        tradeVol: +(t.volume / 1e11).toFixed(2),
                        tradeCount: buys + sells,
                        withdraw: +(a.activity_types?.withdraw?.amount_btc / 1e11).toFixed(3),
                        deposit: +(a.activity_types?.deposit?.amount_btc / 1e11).toFixed(3),
                        buys,
                        sells,
                    };
                });

                setData({
                    summary: {
                        tokens: json.tokens,
                        bonded: json.bonded,
                        vol24h: json.total_volume_24h,
                        users: json.total_users,
                        total_volume_all: json.total_volume_all,
                    },
                    today: combined[combined.length - 1],
                    chartData: combined,
                });
            } catch (error) {
                console.error("Failed to process data:", error);
                // Handle error state if needed
            } finally {
                setLoading(false);
            }
        };
        // Simulate network delay
        const timer = setTimeout(processData, 1500);
        return () => clearTimeout(timer);
    }, []);

    // --- UPDATED LOADING STATE ---
    if (loading) {
        return (
            <Box sx={{ background: theme.palette.background.default, minHeight: '100vh', py: 4 }}>
                <Container maxWidth="xl">
                    {/* <Skeleton variant="text" width={400} height={50} sx={{ mb: 1 }} />
                    <Skeleton variant="text" width={500} height={25} sx={{ mb: 4 }} /> */}

                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        {Array.from(new Array(4)).map((_, index) => (
                            <Grid key={index} item xs={6} md={3}>
                                <StatCardSkeleton />
                            </Grid>
                        ))}
                    </Grid>

                    <Divider sx={{ mb: 4, borderColor: 'rgba(255, 255, 255, 0.12)' }} />

                    <Skeleton variant="text" width={300} height={40} sx={{ mb: 3 }} />

                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        {Array.from(new Array(4)).map((_, index) => (
                            <Grid key={index} item xs={6} md={3}>
                                <StatCardSkeleton />
                            </Grid>
                        ))}
                    </Grid>

                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Paper sx={{ p: { xs: 1.5, md: 3 }, height: 380, display: 'flex', flexDirection: 'column' }}>
                                <Skeleton variant="text" width="40%" height={30} sx={{ mb: 2 }} />
                                <Skeleton variant="rectangular" width="100%" height="100%" sx={{ flex: 1, borderRadius: 1 }} />
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: { xs: 1.5, md: 3 }, height: 380, display: 'flex', flexDirection: 'column' }}>
                                <Skeleton variant="text" width="60%" height={30} sx={{ mb: 2 }} />
                                <Skeleton variant="rectangular" width="100%" height="100%" sx={{ flex: 1, borderRadius: 1 }} />
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: { xs: 1.5, md: 3 }, height: 380, display: 'flex', flexDirection: 'column' }}>
                                <Skeleton variant="text" width="55%" height={30} sx={{ mb: 2 }} />
                                <Skeleton variant="rectangular" width="100%" height="100%" sx={{ flex: 1, borderRadius: 1 }} />
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        );
    }

    // Fallback if data fails to load (optional)
    if (!data) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: theme.palette.background.default }}>
                <Typography color="error">{t('analytics.failedToLoadDashboard')}</Typography>
            </Box>
        );
    }


    const { summary, today, chartData } = data;

    const summaryItems = [
        { label: t('analytics.totalVolumeBTC'), value: formatNumber(summary.total_volume_all / 1e11, locale), icon: <ShowChartIcon />, color: theme.palette.primary.main },
        { label: t('analytics.totalUsers'), value: formatNumber(summary.users, locale), icon: <PeopleAltIcon />, color: theme.palette.secondary.main },
        { label: t('analytics.totalTokens'), value: formatNumber(summary.tokens, locale), icon: <TollIcon />, color: theme.palette.success.main },
        { label: t('analytics.bondedTokens'), value: formatNumber(summary.bonded, locale), icon: <HubIcon />, color: theme.palette.warning.main },
    ];

    const todayItems = [
        { label: t('analytics.withdrawnBTC'), value: formatNumber(today.withdraw, locale), icon: <ArrowDownwardIcon />, color: theme.palette.error.main },
        { label: t('analytics.depositedBTC'), value: formatNumber(today.deposit, locale), icon: <ArrowUpwardIcon />, color: theme.palette.success.main },
        { label: t('analytics.buys'), value: formatNumber(today.buys, locale), icon: <ShoppingCartIcon />, color: theme.palette.info.main },
        { label: t('analytics.sells'), value: formatNumber(today.sells, locale), icon: <SellIcon />, color: theme.palette.warning.main },
    ];

    const CustomTooltip = ({ active, payload, label }) => active && payload?.length ? (
        <Paper sx={{ p: 1.5, background: 'rgba(0,0,0,0.8)', border: `1px solid ${ theme.palette.primary.main }50`, backdropFilter: 'blur(6px)' }}>
            <Typography variant="subtitle2">{label}</Typography>
            <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.2)' }} />
            {payload.map(pld => (
                <Typography key={pld.dataKey} variant="body2" sx={{ color: pld.color, fontWeight: 'medium' }}>
                    {pld.name}: {pld.value.toLocaleString()}
                </Typography>
            ))}
        </Paper>
    ) : null;

    return (
        <Box sx={{ background: theme.palette.background.default, minHeight: '100vh', py: 4 }}>
            <Container maxWidth="xl">

                <Grid container spacing={3} sx={{ mb: 4 }}>
                    {summaryItems.map(({ label, value, icon, color }) => (
                        <Grid key={label} item xs={6} md={3}>
                            <StatCard title={label} value={value} icon={icon} color={color} />
                        </Grid>
                    ))}

                </Grid>

                <Divider sx={{ mb: 4, borderColor: 'rgba(255, 255, 255, 0.12)' }} />

                <Typography variant="h5" gutterBottom>{t('analytics.todaysMetrics')}</Typography>

                <Grid container spacing={3} sx={{ mb: 4 }}>
                    {todayItems.map(({ label, value, icon, color }) => (
                        <Grid key={label} item xs={6} md={3}>
                            <StatCard title={label} value={value} icon={icon} color={color} />
                        </Grid>
                    ))}
                </Grid>

                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <ChartContainer title={t('analytics.tradeVolumeBTC')}>
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="volGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.6} />
                                            <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <XAxis dataKey="date" tick={{ fontSize: 12, fill: theme.palette.text.secondary }} tickLine={false} axisLine={false} />
                                    <YAxis yAxisId="left" tickFormatter={val => `${ val }`} tick={{ fontSize: 12, fill: theme.palette.text.secondary }} tickLine={false} axisLine={false} />
                                    <YAxis yAxisId="right" orientation="right" tickFormatter={(val) => formatNumber(val, locale)} tick={{ fontSize: 12, fill: theme.palette.text.secondary }} tickLine={false} axisLine={false} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend verticalAlign="top" wrapperStyle={{ top: -5 }} />
                                    <Area yAxisId="left" type="monotone" dataKey="tradeVol" name={t('analytics.volumeBTC')} stroke={theme.palette.primary.main} strokeWidth={2} fill="url(#volGradient)" />
                                    <Line yAxisId="right" type="monotone" dataKey="tradeCount" name={t('analytics.tradeCount')} stroke={theme.palette.secondary.main} strokeWidth={2} dot={false} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </ChartContainer>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ChartContainer title={t('analytics.depositsVsWithdrawals')}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                                    <XAxis dataKey="date" tick={{ fontSize: 12, fill: theme.palette.text.secondary }} tickLine={false} axisLine={false} />
                                    <YAxis tickFormatter={val => `${ val }`} tick={{ fontSize: 12, fill: theme.palette.text.secondary }} tickLine={false} axisLine={false} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend verticalAlign="top" wrapperStyle={{ top: -5 }} />
                                    <Line type="monotone" dataKey="deposit" name={t('analytics.deposits')} stroke={theme.palette.success.main} dot={false} strokeWidth={2} />
                                    <Line type="monotone" dataKey="withdraw" name={t('analytics.withdrawals')} stroke={theme.palette.error.main} dot={false} strokeWidth={2} />
                                </LineChart>
                            </ResponsiveContainer>
                        </ChartContainer>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ChartContainer title={t('analytics.buysVsSells')}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartData} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                                    <XAxis dataKey="date" tick={{ fontSize: 12, fill: theme.palette.text.secondary }} tickLine={false} axisLine={false} />
                                    <YAxis tickFormatter={(val) => formatNumber(val, locale)} tick={{ fontSize: 12, fill: theme.palette.text.secondary }} tickLine={false} axisLine={false} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend verticalAlign="top" wrapperStyle={{ top: -5 }} />
                                    <Bar dataKey="buys" name={t('analytics.buys')} fill={theme.palette.info.main} radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="sells" name={t('analytics.sells')} fill={theme.palette.warning.main} radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartContainer>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
}

export default function AnalyticsPage() {
    return (
        <>
            <OdinBalance height={700} />
            <DashboardContent />
        </>
    );
}